import { useNavigate, useParams } from "react-router";
import {
  Box,
  FormControl,
  Grid,
  TextField,
  Link,
  Button,
  CircularProgress,
  styled,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { CardComponent } from "components/card/Card";
import * as yup from "yup";
import { useEffect, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { enqueueSnackbar } from "notistack";
import loadingStore from "core/utils/loading";
import { observer } from "mobx-react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import { ErrorMessage as FormikErrorMessage, Formik } from "formik";
import PageWrapper from "components/PageWrapper";
import { useAuth } from "app/authHooks";
import { hasPermission, Permission } from "core/utils/roles";
import adminStore from "../api";
import { Switch, FormControlLabel } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { validate, version } from "uuid";

function AddEditAdminUsers() {
  const { attributes } = useAuth();
  const navigate = useNavigate();
  const {
    getAdmin,
    createAdmin,
    createS2SAdmin,
    updateAdmin,
    updateS2SAdmin,
    getAdminRoles,
  } = adminStore;
  const { loading } = loadingStore;
  const [signInWithEmail, setSignInWithEmail] = useState(false);
  const [roles, setRoles] = useState<any>([]);

  const canUpdateadmin = hasPermission(
    "update:admin-onboarding" as Permission
  );

  const initialValues = {
    customer_id: "",
    employee_id: "",
    name: "",
    username: "",
    email: "",
    role: "",
    username_provided: true,
    system_to_system: false,
    api_header: "",
    token_time: null,
    user_type: null,
  } as any;

  const adminUserSchema = yup.object({
    customer_id: yup
      .string()
      .min(7, "Customer Id must be at least 7 characters.")
      .max(10, "Customer Id must not exceed 10 characters.")
      .required("Customer Id is required."),

    employee_id: yup
      .string()
      .min(7, "Employee Id must be at least 7 characters.")
      .max(11, "Employee Id must not exceed 11 characters.")
      .required("Employee Id is required."),

    name: yup.string().when("system_to_system", {
      is: true,
      then: (schema) =>
        schema
          .min(2, "Name must be at least 2 characters.")
          .max(50, "Name must not exceed 50 characters.")
          .matches(
            /^(?=^[A-Za-z])[A-Za-z0-9]*(?:[_-]?[A-Za-z0-9]+)*$/,
            "Name must start with a letter and can contain letters, numbers, underscores (_) or dashes (-). It cannot end with _ or -, contain spaces, or have consecutive special characters."
          )
          .required("Name is required."),
      otherwise: (schema) =>
        schema
          .max(50, "Name must not exceed 50 characters.")
          .required("Name is required."),
    }),
    system_to_system: yup.boolean().optional(),
    username_provided: yup
      .boolean()
      .required("The 'username_provided' field is required."),
    username: yup.string().when(["username_provided", "system_to_system"], {
      is: (username_provided: boolean, system_to_system: boolean) =>
        username_provided && !system_to_system,
      then: (schema) =>
        schema
          .min(5, "Username must be at least 5 characters.")
          .max(50, "Username must not exceed 50 characters.")
          .required(
            "Username is required when 'username provided' is true and 'system to system' is false."
          ),
      otherwise: (schema) => schema.optional(),
    }),

    email: yup.string().when("system_to_system", {
      is: true,
      then: (schema) => schema.optional(),
      otherwise: (schema) =>
        schema
          .email("Enter a valid email address.")
          .required("Email is required."),
    }),

    role: yup
      .string()
      .max(30, "Role must not exceed 30 characters.")
      .required("Role is required."),

    token_time: yup
      .number()
      .nullable()
      .when("system_to_system", {
        is: true,
        then: (schema) =>
          schema
            .required("Token Validity is required.")
            .min(5, "Token Validity must be at least 5 minutes.")
            .max(
              1440,
              "Token Validity must be at most 1440 minutes (24 hours)."
            ),
        otherwise: (schema) => schema.optional(),
      }),

    user_type: yup
      .string()
      .nullable()
      .when("system_to_system", {
        is: true,
        then: (schema) =>
          schema
            .required("User type is required when System-to-System is enabled.")
            .oneOf(
              ["ADVISER", "INVESTOR"],
              "User type must be either 'ADVISER' or 'INVESTOR'"
            ),
        otherwise: (schema) => schema.nullable(),
      }),
  });

  const isUuidV4 = (str: string) => validate(str) && version(str) === 4;
  const params = useParams();
  const isEdit = !!params?.id;
  const [dataValue, setDataValue] = useState<any>(initialValues);

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value);
    enqueueSnackbar("Api key header has been Copied!", { variant: "success" });
  };

  useEffect(() => {
    onInit();
  }, []);

  const onInit = async () => {
    setRoles(await getAdminRoles());
    if (isEdit) {
      const adminUser = await getAdmin(params?.id);
      const emailSignIn = isUuidV4(adminUser.username || "");
      setSignInWithEmail(emailSignIn);
      setDataValue({
        ...adminUser,
        username_provided: !emailSignIn,
        email: adminUser.system_to_system ? "N/A" : adminUser.email,
      });
    } else {
      setDataValue(initialValues);
    }
  };

  const handleSubmitForm = async (values: any) => {
    if (isEdit && params?.id) {
      const response = values.system_to_system
        ? await updateS2SAdmin(params.id, {
          customer_id: values.customer_id,
          employee_id: values.employee_id,
          token_time: values.token_time,
          role: values.role,
          user_type: values.user_type,
        })
        : await updateAdmin(params.id, {
          customer_id: values.customer_id,
          employee_id: values.employee_id,
          role: values.role,
          username: values.username,
        });

      if (response === true) {
        enqueueSnackbar("Admin user has been successfully updated!!!", {
          variant: "success",
        });
        navigate("/admin-users");
      } else {
        enqueueSnackbar("Failed to update admin user. Please try again.", {
          variant: "error",
        });
      }
    } else {
      const response = values.system_to_system
        ? await createS2SAdmin({
          customer_id: values.customer_id,
          employee_id: values.employee_id,
          name: values.name,
          token_time: values.token_time,
          role: values.role,
          user_type: values.user_type,
        })
        : await createAdmin({
          customer_id: values.customer_id,
          employee_id: values.employee_id,
          name: values.name,
          email: values.email,
          role: values.role,
          username_provided: values.username_provided,
          ...(values.username_provided && { username: values.username }),
        });
      if (response && response.id) {
        enqueueSnackbar("admin user has been successfully created!!!", {
          variant: "success",
        });
        navigate("/admin-users");
      } else {
        enqueueSnackbar("Failed to create admin user. Please try again.", {
          variant: "error",
        });
      }
    }
  };

  const formatError = (error: any): string | undefined => {
    if (typeof error === "string") {
      return error;
    } else if (Array.isArray(error)) {
      return error.join(", ");
    } else if (typeof error === "object") {
      return "An error occurred";
    }
    return undefined;
  };

  const ErrorMessage = styled(FormikErrorMessage)`
    margin-left: 1rem;
    color: #d32f2f;
    font-size: 0.7rem;
  `;

  const StyledPhoneInput = styled(PhoneInput)`
    .flag-dropdown {
      pointer-events: none;
    }
  `;

  return (
    <PageWrapper>
      <Box sx={{ py: 4, px: 4 }}>
        <Box
          sx={{
            cursor: "pointer",
            fontSize: "14px",
            lineHeight: "20px",
            marginBottom: "16px",
          }}
        >
          <Link underline="hover" onClick={() => navigate("/admin-users")}>
            <ArrowBackIcon
              sx={{ width: "16px", height: "16px", marginRight: "4px" }}
            />{" "}
            Back to Admin Users
          </Link>
        </Box>
        <Formik
          onSubmit={handleSubmitForm}
          initialValues={dataValue}
          validationSchema={adminUserSchema}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            handleChange,
            handleSubmit,
            touched,
            handleBlur,
            setFieldValue,
          }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Switch
                            disabled={isEdit}
                            checked={values.system_to_system}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFieldValue("system_to_system", isChecked);
                            }}
                          />
                        }
                        label="System To System"
                        sx={{ mb: 2 }}
                      />
                    </Grid>
                    {!values.system_to_system && (
                      <Grid item>
                        <FormControlLabel
                          control={
                            <Switch
                              disabled={isEdit}
                              checked={!values.username_provided}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setSignInWithEmail(isChecked);
                                setFieldValue("username_provided", !isChecked);

                                if (isChecked) {
                                  setFieldValue("username", "");
                                }
                              }}
                            />
                          }
                          label="Sign in with Email"
                          sx={{ mb: 2 }}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <CardComponent
                    title={
                      isEdit
                        ? canUpdateadmin
                          ? "Edit Admin User"
                          : "View Admin User"
                        : "Add Admin User"
                    }
                    cardContent={
                      <Box sx={{ "& > *:not(:last-child)": { mb: 8 } }}>
                        <Grid container spacing={2}>
                          <Grid
                            item
                            md={6}
                            xs={10}
                            style={{ textAlign: "left" }}
                          >
                            <FormControl style={{ width: "100%" }}>
                              <TextField
                                fullWidth
                                type="text"
                                label="Customer Id"
                                name="customer_id"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.customer_id}
                                error={
                                  !!touched.customer_id && !!errors?.customer_id
                                }
                                helperText={
                                  touched?.customer_id
                                    ? formatError(errors?.customer_id)
                                    : undefined
                                }
                                sx={{
                                  gridColumn: "span 4",
                                }}
                                InputLabelProps={{ shrink: true }}
                                disabled={!canUpdateadmin}
                              />
                            </FormControl>
                          </Grid>
                          <Grid
                            item
                            md={6}
                            xs={10}
                            style={{ textAlign: "left" }}
                          >
                            <FormControl style={{ width: "100%" }}>
                              <TextField
                                fullWidth
                                type="text"
                                label="Employee Id"
                                name="employee_id"
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.employee_id}
                                error={
                                  !!touched.employee_id && !!errors?.employee_id
                                }
                                helperText={
                                  touched?.employee_id
                                    ? formatError(errors?.employee_id)
                                    : undefined
                                }
                                sx={{
                                  gridColumn: "span 4",
                                }}
                                InputLabelProps={{ shrink: true }}
                                disabled={!canUpdateadmin}
                              />
                            </FormControl>
                          </Grid>
                          <Grid
                            item
                            md={6}
                            xs={10}
                            style={{ textAlign: "left" }}
                          >
                            <FormControl style={{ width: "100%" }}>
                              <TextField
                                fullWidth
                                type="text"
                                label="Name"
                                name="name"
                                disabled={isEdit || !canUpdateadmin}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.name}
                                error={!!touched.name && !!errors?.name}
                                helperText={
                                  touched?.name
                                    ? formatError(errors?.name)
                                    : undefined
                                }
                                sx={{
                                  gridColumn: "span 4",
                                }}
                                InputLabelProps={{ shrink: true }}
                              />
                            </FormControl>
                          </Grid>
                          {values.username_provided &&
                            !values.system_to_system && (
                            <Grid item md={6} xs={12}>
                              <TextField
                                fullWidth
                                label="Username"
                                name="username"
                                disabled={isEdit || !canUpdateadmin}
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.username}
                                error={
                                  !!touched.username && !!errors.username
                                }
                                helperText={
                                  touched.username &&
                                    typeof errors.username === "string"
                                    ? errors.username
                                    : ""
                                }
                              />
                            </Grid>
                          )}
                          {!values.system_to_system && (
                            <Grid
                              item
                              md={6}
                              xs={10}
                              style={{ textAlign: "left" }}
                            >
                              <FormControl style={{ width: "100%" }}>
                                <TextField
                                  fullWidth
                                  type="text"
                                  label="Email"
                                  name="email"
                                  disabled={isEdit || !canUpdateadmin}
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  value={values.email}
                                  error={!!touched.email && !!errors?.email}
                                  helperText={
                                    touched?.email
                                      ? formatError(errors?.email)
                                      : undefined
                                  }
                                  sx={{
                                    gridColumn: "span 4",
                                  }}
                                  InputLabelProps={{ shrink: true }}
                                />
                              </FormControl>
                            </Grid>
                          )}
                          <Grid
                            item
                            md={6}
                            xs={10}
                            style={{ textAlign: "left" }}
                          >
                            <FormControl
                              style={{ width: "100%" }}
                              error={!!touched.role && !!errors?.role}
                            >
                              <InputLabel id="role_label">Role</InputLabel>
                              <Select
                                fullWidth
                                labelId="role_label"
                                label="role"
                                placeholder="Role"
                                name="role"
                                value={values.role}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                disabled={!canUpdateadmin}
                              >
                                {roles.map((role: any) => (
                                  <MenuItem key={role.id} value={role.role_code}>
                                    {role.name}
                                  </MenuItem>
                                ))}
                              </Select>
                              {!!touched.role && !!errors?.role && (
                                <FormHelperText>
                                  {formatError(errors.role)}
                                </FormHelperText>
                              )}
                            </FormControl>
                          </Grid>
                          {(values.system_to_system === true ||
                            values.system_to_system === 1) && (
                            <>
                              <Grid
                                item
                                md={6}
                                xs={10}
                                style={{ textAlign: "left" }}
                              >
                                <FormControl
                                  style={{ width: "100%" }}
                                  error={
                                    !!touched.user_type && !!errors?.user_type
                                  }
                                >
                                  <InputLabel id="user_type_label">
                                    User Type
                                  </InputLabel>
                                  <Select
                                    fullWidth
                                    labelId="user_type_label"
                                    label="User Type"
                                    name="user_type"
                                    value={values.user_type}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    disabled={!canUpdateadmin}
                                  >
                                    <MenuItem value="ADVISER">ADVISER</MenuItem>
                                    <MenuItem value="INVESTOR">
                                      INVESTOR
                                    </MenuItem>
                                  </Select>
                                  {!!touched.user_type &&
                                    !!errors?.user_type && (
                                    <FormHelperText>
                                      {formatError(errors.user_type)}
                                    </FormHelperText>
                                  )}
                                </FormControl>
                              </Grid>
                            </>
                          )}
                          {isEdit && (
                            <Grid
                              item
                              md={6}
                              xs={10}
                              style={{ textAlign: "left" }}
                            >
                              <FormControl style={{ width: "100%" }}>
                                <TextField
                                  fullWidth
                                  type="text"
                                  label="API Key Header"
                                  name="api_header"
                                  onBlur={handleBlur}
                                  onChange={handleChange}
                                  value={values.api_header}
                                  InputLabelProps={{ shrink: true }}
                                  disabled={!canUpdateadmin}
                                  InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label="copy api key"
                                          onClick={() =>
                                            handleCopy(values.api_header)
                                          }
                                          edge="end"
                                          disabled={!values.api_header}
                                        >
                                          <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </FormControl>
                            </Grid>
                          )}
                          {(values.system_to_system === true ||
                            values.system_to_system === 1) && (
                            <>
                              <Grid
                                item
                                md={6}
                                xs={10}
                                style={{ textAlign: "left" }}
                              >
                                <FormControl style={{ width: "100%" }}>
                                  <TextField
                                    fullWidth
                                    type="number"
                                    label="Token Validity (minutes)"
                                    name="token_time"
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    value={values.token_time}
                                    error={
                                      !!touched.token_time &&
                                      !!errors?.token_time
                                    }
                                    helperText={
                                      touched.token_time
                                        ? formatError(errors?.token_time)
                                        : undefined
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    disabled={!canUpdateadmin}
                                    inputProps={{ min: 1, max: 1440 }}
                                  />
                                </FormControl>
                              </Grid>
                            </>
                          )}
                        </Grid>
                      </Box>
                    }
                  />
                  {canUpdateadmin && (
                    <Box
                      width="100%"
                      display="flex"
                      mt="10px"
                      mb="10px"
                      mr="10px"
                    >
                      <Button
                        style={{ marginTop: 10, width: "100px" }}
                        type="submit"
                        color="primary"
                        variant="contained"
                        disabled={loading}
                        endIcon={
                          loading ? <CircularProgress size={20} /> : null
                        }
                      >
                        {isEdit
                          ? loading
                            ? "UPDATING"
                            : "UPDATE"
                          : loading
                            ? "SAVING"
                            : "SAVE"}
                      </Button>
                    </Box>
                  )}
                </Box>
              </form>
            );
          }}
        </Formik>
      </Box>
    </PageWrapper>
  );
}

export default observer(AddEditAdminUsers);
